import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { AdvertiserCampaignProvider } from 'sonata-platform-components';
import {
  ButtonUI,
  Input,
  ModalUI,
  showNotification,
  useQueryFetchClearCache,
  type ValidationErrors,
  validationErrorChecker
} from 'sonata-react-components';
import { MediaAdvertiserUserValidator } from 'sonata-static-storage/lib/schemas/mediaInfo/mediaAdvertiserUser.validator';
import { z } from 'zod';

import { useCampaignPlan } from '@/context/CampaignPlanProvider';
import { useModals } from '@/router';
import { generateAdvertiser, updateMediaAdvertiser } from '@/services/advertiser.service';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors.ts';
import { getUserRoles } from '@/store/global/userRoles/selectors.ts';
import { type CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';
import { AdvertiserFormStyled } from './AdvertiserForm.styled';
import { AdvertiserLogoWrapper } from './AdvertiserLogoWrapper/AdvertiserLogoWrapper';
import { AgencyGroupSelectors } from './AgencyGroupSelectors';
import { Advertiser } from './types';

const NewMediaAdvertiserUserValidator = MediaAdvertiserUserValidator.extend({
  created: z.date().optional(),
  deleted: z.boolean().optional(),
  url: z.string().refine(url => taptap?.tools?.validateUrl(url))
});

type Props = {
  advertiser: Partial<Advertiser>;
};

type State = {
  errors?: ValidationErrors;
  hasAttemptedSubmit: boolean;
  mediaInfoSelected?: Record<string, any>;
  newAdvertiserName: string;
  newAdvertiserUrl: string;
};

export default function AdvertiserForm({ advertiser }: Props) {
  const { formatMessage } = useIntl();
  const { mediaAdvertiserUser, setMediaAdvertiser, userId } = useCampaignPlan();
  const { setCacheSeed, setHasAdvertisers } = useCampaignStore() as CampaignsStore;
  const { clearQueryCache } = useQueryFetchClearCache(['advertisersSelector']);
  const { clearQueryCache: clearMediaInfoDictionariesCache } = useQueryFetchClearCache('media-info-dictionaries');
  const modals = useModals();
  const modalRef: any = useRef();
  const createToken = useRef({} as Record<string, any>);
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);

  const [{ errors, hasAttemptedSubmit, mediaInfoSelected, newAdvertiserName, newAdvertiserUrl }, setState] =
    useState<State>({
      hasAttemptedSubmit: false,
      newAdvertiserName: advertiser?.name || '',
      newAdvertiserUrl: advertiser?.url || ''
    });

  useEffect(() => {
    modalRef?.current?.openModal();
  }, [modalRef]);

  useEffect(() => {
    return () => {
      createToken.current?.cancel?.();
    };
  }, []);

  const handleMediaInfoChange = (value: Record<string, any>) => {
    setState(prevState => ({
      ...prevState,
      mediaInfoSelected: value
    }));
  };

  const isEdition = Boolean(advertiser._id);

  const createNewMediaAdvertiser = async () => {
    try {
      await generateAdvertiser(userId, {
        mediaAgency: mediaInfoSelected?.agency?._id || undefined,
        mediaGroup: mediaInfoSelected?.group?._id || undefined,
        name: newAdvertiserName,
        url: newAdvertiserUrl
      });
      setHasAdvertisers(true);
      setCacheSeed();
      clearQueryCache();
      clearMediaInfoDictionariesCache();
      showNotification(
        'notify',
        formatMessage({
          defaultMessage: 'Advertiser created successfully',
          id: 'advertisers.modal.created'
        })
      );
    } catch (_e: any) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Failure creating the Advertiser',
          id: 'advertisers.modal.createdError'
        })
      );
    }
  };

  const updateAdvertiser = async () => {
    try {
      await updateMediaAdvertiser(userId, advertiser._id as string, {
        name: newAdvertiserName,
        url: newAdvertiserUrl
      });
      setCacheSeed();
      showNotification(
        'notify',
        formatMessage({
          defaultMessage: 'Advertiser edited successfully',
          id: 'advertisers.modal.updated'
        })
      );
    } catch (_e: any) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Failure editing the Advertiser',
          id: 'advertisers.modal.updatedError'
        })
      );
    }
  };

  const areValidationsOk = (name = newAdvertiserName, url = newAdvertiserUrl): boolean => {
    const validationErrors = validationErrorChecker(
      {
        name,
        url
      },
      NewMediaAdvertiserUserValidator
    );

    setState(prev => {
      return {
        ...prev,
        errors: validationErrors
      };
    });

    return isEmpty(validationErrors);
  };

  const handleAdvertiserNameChange = (text: string | FileList | null) => {
    const name = text as string;
    setState(prev => ({
      ...prev,
      newAdvertiserName: name
    }));
    if (hasAttemptedSubmit) {
      areValidationsOk(name, newAdvertiserUrl);
    }
  };

  const onChangeNewAdvertiserUrl = (url: string | FileList | null) => {
    const newUrl = url as string;
    setState(prev => ({
      ...prev,
      newAdvertiserUrl: newUrl
    }));
    if (hasAttemptedSubmit) {
      areValidationsOk(newAdvertiserName, newUrl);
    }
  };

  const handleMediaAdvertiserChange = (mediaAdvertiserId: string) => {
    setMediaAdvertiser(mediaAdvertiserId);
  };

  const handleCloseModal = () =>
    modals.close({
      at: {
        pathname: '/campaigns',
        search: location.search
      }
    });

  const handleAdvertiserButtonClick = () => {
    setState(prev => ({ ...prev, hasAttemptedSubmit: true }));
    if (areValidationsOk()) {
      isEdition ? updateAdvertiser() : createNewMediaAdvertiser();
      handleCloseModal();
    }
  };

  return (
    <AdvertiserFormStyled className="advertiserModal">
      <ModalUI
        centerButton={
          <ButtonUI
            disabled={
              (hasAttemptedSubmit && !isEmpty(errors)) ||
              newAdvertiserName.trim() === '' ||
              newAdvertiserUrl.trim() === ''
            }
            icon={`fa ${isEdition ? 'fa-floppy-disk' : 'fa-plus'}`}
            iconPosition="left"
            id="advertiserSettingsBtn"
            onClick={handleAdvertiserButtonClick}
            shape="squareRounded"
            text={
              isEdition
                ? formatMessage({
                    defaultMessage: 'Save',
                    id: 'common.save'
                  })
                : formatMessage({
                    defaultMessage: 'Create Advertiser',
                    id: 'advertisers.modal.createAdvertiser'
                  })
            }
            type="primary"
          />
        }
        closeModalCallback={handleCloseModal}
        ref={modalRef}
        title={formatMessage({
          defaultMessage: 'Create Advertiser',
          id: `advertisers.modal.title.${isEdition ? 'edit' : 'create'}`
        })}
        width="849px"
      >
        <div className="advertiserModal__rows">
          <Input
            description={
              errors?.name
                ? formatMessage({
                    defaultMessage: 'Advertiser name should have at least 3 characters',
                    id: 'advertisers.modal.form.name.error'
                  })
                : undefined
            }
            id="advertiserModal-campaignName"
            inputStatus={errors?.name ? 'wrong' : 'default'}
            label={formatMessage({
              defaultMessage: 'Advertiser Name',
              id: 'advertisers.modal.form.name.label'
            })}
            name="advertiserModal-campaignName"
            onChange={handleAdvertiserNameChange}
            placeholder={formatMessage({
              defaultMessage: 'Insert Advertiser Name',
              id: 'advertisers.modal.form.name.placeholder'
            })}
            required
            shape="squareUI"
            type="text"
            value={newAdvertiserName}
          />
          <AdvertiserCampaignProvider
            avoidDeletedEntities={true}
            campaignMediaAdvertiser={mediaAdvertiserUser}
            mediaInfoSelected={mediaInfoSelected}
            onMediaAdvertiserChange={handleMediaAdvertiserChange}
            onMediaInfoSelectedChange={handleMediaInfoChange}
            user={user.toJSON()}
            userRoles={userRoles}
            validationError={errors?.mediaAdvertiserUser}
          >
            {advertiser?._id && (
              <AdvertiserLogoWrapper advertiser={advertiser} user={user.toJSON()} userRoles={userRoles} />
            )}
            <AgencyGroupSelectors isDraft={!isEdition} />
          </AdvertiserCampaignProvider>
          <Input
            description={
              errors?.url
                ? formatMessage({
                    defaultMessage: 'Advertiser name should have at least 3 characters',
                    id: 'advertisers.modal.form.url.error'
                  })
                : undefined
            }
            disabled={isEdition}
            id="advertiserUrlInput"
            inputStatus={errors?.url ? 'wrong' : 'default'}
            label={formatMessage({
              defaultMessage: 'URL',
              id: 'advertisers.modal.form.url.label'
            })}
            name="advertiserUrlInput"
            notFloatingPlaceholder
            onChange={onChangeNewAdvertiserUrl}
            placeholder={formatMessage({
              defaultMessage: 'http://example.com',
              id: 'advertisers.modal.form.placeholders.url'
            })}
            required
            shape="square"
            type="text"
            value={newAdvertiserUrl}
          />
        </div>
      </ModalUI>
    </AdvertiserFormStyled>
  );
}
